// Çizim geometrisi: tarayıcıdaki canvas çizimi ile sunucudaki PDF dışa aktarımı aynı
// fonksiyonları kullanır; böylece ekranda görülen ile PDF'ye işlenen birebir aynı olur.
import type { Annotation, InkTool, Point, PolygonKind, ShapeKind } from './protocol';

export const TEXT_LINE_HEIGHT = 1.25;
/** Satır üstünden taban çizgisine uzaklık (em). DejaVu Sans + line-height 1.25 için. */
export const TEXT_BASELINE = 0.97;
export const ANNOTATION_FONT = 'DejaVu Sans';

/** 0=moveTo 1=lineTo 2=quadratic 3=cubic 4=close */
export type PathCmd =
  | [0, number, number]
  | [1, number, number]
  | [2, number, number, number, number]
  | [3, number, number, number, number, number, number]
  | [4];

export interface PathSink {
  moveTo(x: number, y: number): void;
  lineTo(x: number, y: number): void;
  quadraticCurveTo(cx: number, cy: number, x: number, y: number): void;
  bezierCurveTo(c1x: number, c1y: number, c2x: number, c2y: number, x: number, y: number): void;
  closePath(): void;
}

export function replayPath(path: readonly PathCmd[], sink: PathSink): void {
  for (const c of path) {
    switch (c[0]) {
      case 0:
        sink.moveTo(c[1], c[2]);
        break;
      case 1:
        sink.lineTo(c[1], c[2]);
        break;
      case 2:
        sink.quadraticCurveTo(c[1], c[2], c[3], c[4]);
        break;
      case 3:
        sink.bezierCurveTo(c[1], c[2], c[3], c[4], c[5], c[6]);
        break;
      case 4:
        sink.closePath();
        break;
    }
  }
}

// ---------------------------------------------------------------- kalem izi

export interface InkRun {
  width: number;
  path: PathCmd[];
}
export interface InkDot {
  x: number;
  y: number;
  r: number;
}
export interface InkGeometry {
  runs: InkRun[];
  dots: InkDot[];
}

const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);

export function hasRealPressure(points: readonly Point[]): boolean {
  for (const p of points) if (p[2] !== 0.5) return true;
  return false;
}

/** Nokta başına çizgi kalınlığı. Kalem basıncı varsa kalınlık basınçla değişir. */
export function inkWidths(tool: InkTool, size: number, points: readonly Point[]): number[] {
  if (tool === 'highlighter' || !hasRealPressure(points)) return points.map(() => size);
  const raw = points.map((p) => size * (0.3 + 0.95 * clamp01(p[2])));
  const n = raw.length;
  return raw.map((w, i) => (raw[Math.max(0, i - 1)] + 2 * w + raw[Math.min(n - 1, i + 1)]) / 4);
}

/**
 * Noktaları orta noktalardan geçen ikinci derece eğrilerle yumuşatır. Kalınlığı aynı olan
 * ardışık parçalar tek bir yol (run) olarak birleştirilir; sabit kalınlıkta tüm iz tek yoldur.
 */
export function inkGeometry(tool: InkTool, size: number, points: readonly Point[]): InkGeometry {
  const n = points.length;
  const runs: InkRun[] = [];
  if (n === 0) return { runs, dots: [] };
  const widths = inkWidths(tool, size, points);
  if (n === 1) return { runs, dots: [{ x: points[0][0], y: points[0][1], r: widths[0] / 2 }] };

  const step = Math.max(0.15, size * 0.06);
  let current: InkRun | null = null;
  const push = (width: number, fromX: number, fromY: number, cmd: PathCmd) => {
    const w = Math.max(0.3, Math.round(width / step) * step);
    let run: InkRun | null = current;
    if (!run || run.width !== w) {
      run = { width: w, path: [[0, fromX, fromY]] };
      runs.push(run);
      current = run;
    }
    run.path.push(cmd);
  };

  if (n === 2) {
    push((widths[0] + widths[1]) / 2, points[0][0], points[0][1], [1, points[1][0], points[1][1]]);
    return { runs, dots: [] };
  }
  let sx = points[0][0];
  let sy = points[0][1];
  for (let i = 1; i <= n - 2; i++) {
    const ex = (points[i][0] + points[i + 1][0]) / 2;
    const ey = (points[i][1] + points[i + 1][1]) / 2;
    push(widths[i], sx, sy, [2, points[i][0], points[i][1], ex, ey]);
    sx = ex;
    sy = ey;
  }
  push(widths[n - 1], sx, sy, [1, points[n - 1][0], points[n - 1][1]]);
  return { runs, dots: [] };
}

// ---------------------------------------------------------------- şekiller

export interface ShapeLike {
  shape: ShapeKind;
  size: number;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

export function arrowHead(x1: number, y1: number, x2: number, y2: number, size: number) {
  const len = Math.max(10, size * 4);
  const ang = Math.atan2(y2 - y1, x2 - x1);
  const spread = Math.PI / 7;
  const head: [number, number][] = [
    [x2, y2],
    [x2 - len * Math.cos(ang - spread), y2 - len * Math.sin(ang - spread)],
    [x2 - len * Math.cos(ang + spread), y2 - len * Math.sin(ang + spread)],
  ];
  const back = Math.min(len * Math.cos(spread) * 0.85, Math.hypot(x2 - x1, y2 - y1));
  const shaftEnd: [number, number] = [x2 - back * Math.cos(ang), y2 - back * Math.sin(ang)];
  return { head, shaftEnd };
}

export function ellipseCurves(x1: number, y1: number, x2: number, y2: number) {
  const cx = (x1 + x2) / 2;
  const cy = (y1 + y2) / 2;
  const rx = Math.abs(x2 - x1) / 2;
  const ry = Math.abs(y2 - y1) / 2;
  const k = 0.5522847498;
  const ox = rx * k;
  const oy = ry * k;
  const start: [number, number] = [cx + rx, cy];
  const curves: [number, number, number, number, number, number][] = [
    [cx + rx, cy + oy, cx + ox, cy + ry, cx, cy + ry],
    [cx - ox, cy + ry, cx - rx, cy + oy, cx - rx, cy],
    [cx - rx, cy - oy, cx - ox, cy - ry, cx, cy - ry],
    [cx + ox, cy - ry, cx + rx, cy - oy, cx + rx, cy],
  ];
  return { cx, cy, rx, ry, start, curves };
}

/** Çemberin içine çizilmiş düzgün çokgen, birim kutuda; Shift ile sürüklenince gerçekten düzgün olur. */
function regular(sides: number, startDeg: number): [number, number][] {
  return Array.from({ length: sides }, (_, i) => {
    const t = ((startDeg + (i * 360) / sides) * Math.PI) / 180;
    return [0.5 + 0.5 * Math.cos(t), 0.5 + 0.5 * Math.sin(t)];
  });
}

/** Köşeler sürükleme kutusuna oranla (0..1) verilir; ters yöne sürüklemek şekli aynalar. */
export const POLYGONS: Record<PolygonKind, { label: string; vertices: [number, number][] }> = {
  triangle: { label: 'Üçgen', vertices: [[0.5, 0], [1, 1], [0, 1]] },
  rightTriangle: { label: 'Dik üçgen', vertices: [[0, 0], [1, 1], [0, 1]] },
  trapezoid: { label: 'Yamuk', vertices: [[0.25, 0], [0.75, 0], [1, 1], [0, 1]] },
  parallelogram: { label: 'Paralelkenar', vertices: [[0.25, 0], [1, 0], [0.75, 1], [0, 1]] },
  rhombus: { label: 'Eşkenar dörtgen', vertices: [[0.5, 0], [1, 0.5], [0.5, 1], [0, 0.5]] },
  pentagon: { label: 'Beşgen', vertices: regular(5, -90) },
  hexagon: { label: 'Altıgen', vertices: regular(6, 0) },
  octagon: { label: 'Sekizgen', vertices: regular(8, 22.5) },
};

export function isPolygon(shape: ShapeKind): shape is PolygonKind {
  return Object.hasOwn(POLYGONS, shape);
}

export function polygonVertices(kind: PolygonKind, x1: number, y1: number, x2: number, y2: number): [number, number][] {
  return POLYGONS[kind].vertices.map(([u, v]) => [x1 + u * (x2 - x1), y1 + v * (y2 - y1)]);
}

export interface ShapePaths {
  stroke: PathCmd[];
  fill: PathCmd[] | null;
  join: 'round' | 'miter';
}

export function shapePaths(s: ShapeLike): ShapePaths {
  const { x1, y1, x2, y2 } = s;
  if (isPolygon(s.shape)) {
    const [first, ...rest] = polygonVertices(s.shape, x1, y1, x2, y2);
    const stroke: PathCmd[] = [[0, first[0], first[1]], ...rest.map(([x, y]): PathCmd => [1, x, y]), [4]];
    return { stroke, fill: null, join: 'round' };
  }
  switch (s.shape) {
    case 'line':
      return { stroke: [[0, x1, y1], [1, x2, y2]], fill: null, join: 'round' };
    case 'arrow': {
      const { head, shaftEnd } = arrowHead(x1, y1, x2, y2, s.size);
      return {
        stroke: [[0, x1, y1], [1, shaftEnd[0], shaftEnd[1]]],
        fill: [[0, head[0][0], head[0][1]], [1, head[1][0], head[1][1]], [1, head[2][0], head[2][1]], [4]],
        join: 'round',
      };
    }
    case 'rect':
      return { stroke: [[0, x1, y1], [1, x2, y1], [1, x2, y2], [1, x1, y2], [4]], fill: null, join: 'miter' };
    case 'ellipse': {
      const e = ellipseCurves(x1, y1, x2, y2);
      const stroke: PathCmd[] = [[0, e.start[0], e.start[1]]];
      for (const c of e.curves) stroke.push([3, c[0], c[1], c[2], c[3], c[4], c[5]]);
      stroke.push([4]);
      return { stroke, fill: null, join: 'round' };
    }
  }
}

// ---------------------------------------------------------------- metin

export function textBaselineY(top: number, fontSize: number, line: number): number {
  return top + fontSize * (line * TEXT_LINE_HEIGHT + TEXT_BASELINE);
}

export function textExtent(text: string, fontSize: number) {
  const lines = text.split('\n');
  const longest = lines.reduce((m, l) => Math.max(m, l.length), 1);
  return { width: longest * fontSize * 0.62, height: lines.length * fontSize * TEXT_LINE_HEIGHT };
}

// ---------------------------------------------------------------- silgi isabet testi

export type BBox = [number, number, number, number];
const bboxCache = new WeakMap<Annotation, BBox>();

export function annotationBBox(a: Annotation): BBox {
  const cached = bboxCache.get(a);
  if (cached) return cached;
  let b: BBox;
  switch (a.kind) {
    case 'ink': {
      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;
      for (const p of a.points) {
        if (p[0] < minX) minX = p[0];
        if (p[0] > maxX) maxX = p[0];
        if (p[1] < minY) minY = p[1];
        if (p[1] > maxY) maxY = p[1];
      }
      const pad = a.size;
      b = [minX - pad, minY - pad, maxX + pad, maxY + pad];
      break;
    }
    case 'shape': {
      const pad = a.size + (a.shape === 'arrow' ? a.size * 4 + 10 : 0);
      b = [Math.min(a.x1, a.x2) - pad, Math.min(a.y1, a.y2) - pad, Math.max(a.x1, a.x2) + pad, Math.max(a.y1, a.y2) + pad];
      break;
    }
    case 'text': {
      const { width, height } = textExtent(a.text, a.fontSize);
      b = [a.x, a.y, a.x + width, a.y + height];
      break;
    }
    case 'figure':
      b = [a.x, a.y, a.x + a.w, a.y + a.h];
      break;
  }
  bboxCache.set(a, b);
  return b;
}

export function distToSegment(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy;
  let t = len2 ? ((px - ax) * dx + (py - ay) * dy) / len2 : 0;
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
}

export function hitTest(a: Annotation, x: number, y: number, r: number): boolean {
  const b = annotationBBox(a);
  if (x < b[0] - r || x > b[2] + r || y < b[1] - r || y > b[3] + r) return false;
  switch (a.kind) {
    case 'ink': {
      const tol = r + a.size / 2;
      const pts = a.points;
      if (pts.length === 1) return Math.hypot(x - pts[0][0], y - pts[0][1]) <= tol;
      for (let i = 1; i < pts.length; i++) {
        if (distToSegment(x, y, pts[i - 1][0], pts[i - 1][1], pts[i][0], pts[i][1]) <= tol) return true;
      }
      return false;
    }
    case 'shape': {
      const tol = r + a.size / 2;
      const { x1, y1, x2, y2 } = a;
      if (a.shape === 'line' || a.shape === 'arrow') return distToSegment(x, y, x1, y1, x2, y2) <= tol;
      if (a.shape === 'rect' || isPolygon(a.shape)) {
        const c: [number, number][] = a.shape === 'rect' ? [[x1, y1], [x2, y1], [x2, y2], [x1, y2]] : polygonVertices(a.shape, x1, y1, x2, y2);
        for (let i = 0; i < c.length; i++) {
          const p = c[i];
          const q = c[(i + 1) % c.length];
          if (distToSegment(x, y, p[0], p[1], q[0], q[1]) <= tol) return true;
        }
        return false;
      }
      const { cx, cy, rx, ry } = ellipseCurves(x1, y1, x2, y2);
      let px = cx + rx;
      let py = cy;
      for (let i = 1; i <= 48; i++) {
        const t = (i / 48) * Math.PI * 2;
        const qx = cx + rx * Math.cos(t);
        const qy = cy + ry * Math.sin(t);
        if (distToSegment(x, y, px, py, qx, qy) <= tol) return true;
        px = qx;
        py = qy;
      }
      return false;
    }
    case 'text':
    case 'figure':
      return true; // kutunun içindeyse
  }
}
